import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	Calendar,
	Download,
	FileText,
	Mail,
	MapPin,
	User,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

// Local solicitation type for public portal
interface PublicSolicitation {
	id: string;
	title: string;
	description: string;
	type: string;
	status: string;
	dueDate: string | null;
	publishDate: string | null;
	pointOfContact?: string;
	pointOfContactEmail?: string;
	address?: string;
	memberOrg?: string;
	attachmentCount: number;
}

export const Route = createFileRoute("/public/solicitations/$id")({
	component: PublicSolicitationDetailPage,
});

function PublicSolicitationDetailPage() {
	const { id } = Route.useParams();
	const matchRoute = useMatchRoute();

	// Render child routes (e.g. /bid, /results) as standalone pages
	const isBidRoute = matchRoute({
		to: "/public/solicitations/$id/bid",
		params: { id },
	});
	const isResultsRoute = matchRoute({
		to: "/public/solicitations/$id/results",
		params: { id },
	});
	if (isBidRoute || isResultsRoute) {
		return <Outlet />;
	}

	// Public solicitation would come from a public tRPC endpoint
	// For now, return not found since there's no public API yet
	const solicitation: PublicSolicitation | null = null;

	if (!solicitation) {
		return (
			<div className="min-h-screen bg-gray-50">
				<header className="bg-white border-b">
					<div className="max-w-4xl mx-auto px-4 py-4">
						<Button variant="ghost" asChild>
							<Link to="/public">
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back to Solicitations
							</Link>
						</Button>
					</div>
				</header>
				<main className="max-w-4xl mx-auto px-4 py-12 text-center">
					<h2 className="text-2xl font-bold">Solicitation not found</h2>
					<p className="text-muted-foreground mt-2">
						This solicitation is not available for public viewing.
					</p>
					<Button asChild className="mt-4">
						<Link to="/public">View All Solicitations</Link>
					</Button>
				</main>
			</div>
		);
	}

	const attachments = [
		{ name: "Request for Proposal.pdf", size: "2.4 MB" },
		{ name: "Technical Requirements.docx", size: "1.1 MB" },
		{ name: "Pricing Template.xlsx", size: "245 KB" },
		{ name: "Terms and Conditions.pdf", size: "890 KB" },
	];

	const questions = [
		{
			question: "What is the expected delivery timeline for the equipment?",
			answer: "Equipment should be delivered within 60 days of contract award.",
			date: "2026-01-21",
		},
		{
			question: "Are extended warranties required?",
			answer: "Yes, a minimum 3-year warranty is required for all equipment.",
			date: "2026-01-23",
		},
	];

	// Compute effective status based on dates
	const effectiveStatus = getEffectiveStatus(solicitation.status, solicitation.publishDate, solicitation.dueDate);

	// Show bid results if solicitation is awarded
	const showBidResults = effectiveStatus === "awarded";
	const _solicitationBids = bids.filter((b) => b.solicitationId === id);

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-4xl mx-auto px-4 py-4">
					<Button variant="ghost" asChild>
						<Link to="/public">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Solicitations
						</Link>
					</Button>
				</div>
			</header>

			{/* Content */}
			<main className="max-w-4xl mx-auto px-4 py-8">
				{/* Title Section */}
				<div className="mb-8">
					<div className="flex items-center gap-2 mb-4">
						<Badge variant="outline">{solicitation.type}</Badge>
						<Badge className={getStatusColor(effectiveStatus)}>
							{getStatusLabel(effectiveStatus)}
						</Badge>
					</div>
					<h1 className="text-3xl font-bold mb-4">{solicitation.title}</h1>
					<p className="text-lg text-muted-foreground">
						{solicitation.description}
					</p>
				</div>

				{/* Key Info Cards */}
				<div className="grid gap-4 md:grid-cols-3 mb-8">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2 text-muted-foreground mb-1">
								<Calendar className="h-4 w-4" />
								<span className="text-sm">Due Date</span>
							</div>
							<p className="text-xl font-semibold">
								{new Date(`${solicitation.dueDate}T00:00:00`).toLocaleDateString()}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2 text-muted-foreground mb-1">
								<FileText className="h-4 w-4" />
								<span className="text-sm">Documents</span>
							</div>
							<p className="text-xl font-semibold">
								{solicitation.attachmentCount} files
							</p>
						</CardContent>
					</Card>
				</div>

				<div className="grid gap-6 md:grid-cols-3">
					<div className="md:col-span-2 space-y-6">
						{/* Documents */}
						<Card>
							<CardHeader>
								<CardTitle>Documents</CardTitle>
								<CardDescription>
									Download solicitation documents
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{attachments.map((file, index) => (
										<div
											key={index}
											className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
										>
											<div className="flex items-center gap-3">
												<FileText className="h-5 w-5 text-muted-foreground" />
												<div>
													<p className="font-medium">{file.name}</p>
													<p className="text-sm text-muted-foreground">
														{file.size}
													</p>
												</div>
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													toast.success(`Downloading ${file.name}...`)
												}
											>
												<Download className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</CardContent>
						</Card>

						{/* Q&A */}
						<Card>
							<CardHeader>
								<CardTitle>Questions & Answers</CardTitle>
								<CardDescription>
									Published responses to vendor inquiries
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{questions.map((qa, index) => (
									<div key={index} className="space-y-2">
										<div className="bg-muted/50 p-3 rounded-lg">
											<p className="font-medium">Q: {qa.question}</p>
										</div>
										<div className="ml-4 p-3 border-l-2 border-primary">
											<p>A: {qa.answer}</p>
											<p className="text-xs text-muted-foreground mt-2">
												Answered on {qa.date}
											</p>
										</div>
										{index < questions.length - 1 && (
											<Separator className="my-4" />
										)}
									</div>
								))}
							</CardContent>
						</Card>

						{/* Bid Results (if awarded) */}
						{showBidResults && (
							<Card>
								<CardHeader>
									<CardTitle>Bid Results</CardTitle>
									<CardDescription>
										Public bid tabulation results
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-2">
										{[
											{
												vendor: "Acme Office Supplies",
												amount: "$87,500",
												status: "Awarded",
											},
											{
												vendor: "Office Depot Inc.",
												amount: "$92,000",
												status: "Not Selected",
											},
											{
												vendor: "Staples Business",
												amount: "$89,750",
												status: "Not Selected",
											},
										].map((bid, index) => (
											<div
												key={index}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div>
													<p className="font-medium">{bid.vendor}</p>
													<p className="text-sm text-muted-foreground">
														{bid.amount}
													</p>
												</div>
												<Badge
													variant={
														bid.status === "Awarded" ? "default" : "secondary"
													}
												>
													{bid.status}
												</Badge>
											</div>
										))}
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Contact Info */}
						<Card>
							<CardHeader>
								<CardTitle>Contact Information</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-start gap-3">
									<User className="h-5 w-5 text-muted-foreground mt-0.5" />
									<div>
										<p className="text-sm text-muted-foreground">
											Point of Contact
										</p>
										<p className="font-medium">{solicitation.pointOfContact}</p>
									</div>
								</div>
								<div className="flex items-start gap-3">
									<Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
									<div>
										<p className="text-sm text-muted-foreground">Email</p>
										<a
											href={`mailto:${solicitation.pointOfContactEmail}`}
											className="font-medium text-primary hover:underline"
										>
											{solicitation.pointOfContactEmail}
										</a>
									</div>
								</div>
								<div className="flex items-start gap-3">
									<MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
									<div>
										<p className="text-sm text-muted-foreground">Location</p>
										<p className="font-medium">{solicitation.address}</p>
									</div>
								</div>
								{solicitation.memberOrg && (
									<div className="flex items-start gap-3">
										<Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
										<div>
											<p className="text-sm text-muted-foreground">
												Department
											</p>
											<p className="font-medium">{solicitation.memberOrg}</p>
										</div>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Submit Bid CTA */}
						{effectiveStatus === "open" && (
							<Card className="bg-primary/5 border-primary/20">
								<CardContent className="pt-6">
									<h3 className="font-semibold mb-2">Interested in bidding?</h3>
									<p className="text-sm text-muted-foreground mb-4">
										Log in to the vendor portal to submit your bid for this opportunity.
									</p>
									<Button className="w-full" asChild>
										<a href="https://app.bidscope.ai/purchasing">
											Submit Bid
										</a>
									</Button>
								</CardContent>
							</Card>
						)}

						{/* Key Dates */}
						<Card>
							<CardHeader>
								<CardTitle>Key Dates</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								{solicitation.publishDate && (
									<div className="flex items-center justify-between">
										<span className="text-sm text-muted-foreground">
											Published
										</span>
										<span className="font-medium">
											{new Date(solicitation.publishDate).toLocaleDateString()}
										</span>
									</div>
								)}
								<div className="flex items-center justify-between">
									<span className="text-sm text-muted-foreground">
										Due Date
									</span>
									<span className="font-medium">
										{new Date(`${solicitation.dueDate}T00:00:00`).toLocaleDateString()}
									</span>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</main>

			{/* Footer */}
			<footer className="bg-white border-t mt-12">
				<div className="max-w-4xl mx-auto px-4 py-8 text-center text-sm text-muted-foreground">
					Powered by Bidscope - Government Procurement Platform
				</div>
			</footer>
		</div>
	);
}
